import React, { useEffect, useMemo, useState } from 'react'
import { sportCreationStore } from 'state/listing/sportListing'
import {
  useDeleteLeagueSport,
  useGetLeagueSportsListing,
} from 'api/Listings/LeagueSports'
import Spinner from 'components/Spinner'
import { Table } from 'antd'
import { PencilSimpleLine, Trash } from '@phosphor-icons/react'
import moment from 'moment'
import { SportListingType } from 'types/listing/listings'
import AlertModal from 'helper/AlertModal'

interface LeagueSportsTableProps {
  id: string
}

const LeagueSportsTable: React.FC<LeagueSportsTableProps> = ({ id }) => {
  const [tableData, setTableData] = useState([])
  const [alertModal, setAlertModal] = useState<boolean>(false)
  const { setAddSportModal, setSportData, sportData } = sportCreationStore(
    store => ({
      setAddSportModal: store.setAddSportModal,
      setSportData: store.setSportData,
      sportData: store.sportData,
    }),
  )

  const { data, isLoading, isFetching, refetch } = useGetLeagueSportsListing()
  const { mutate: leagueDelete, isLoading: leagueDeleteLoading } =
    useDeleteLeagueSport()

  const handleEditClick = (data: SportListingType) => {
    setSportData(data)
    setAddSportModal(true)
  }

  const handleDeleteClick = (data: SportListingType) => {
    setSportData(data)
    setAlertModal(true)
  }

  const handleYesClick = () => {
    leagueDelete(sportData._id, {
      onSuccess: () => {
        setSportData(undefined)
        setAlertModal(false)
      },
    })
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <p className="text-14 w-400">{text}</p>,
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      render: (text: string) => (
        <img src={text} alt="icon" className="w-30px h-30px radius-rounded" />
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text: string) => (
        <p className="text-14 w-400">{moment(text).format('DD MMM, YYYY')}</p>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: '100px',
      render: (data: any) => (
        <div className="flex align-center gap-20px">
          <PencilSimpleLine
            size={20}
            color="#000"
            className="pointer"
            onClick={() => handleEditClick(data)}
          />
          <Trash
            size={20}
            color="#FF6961"
            className="pointer"
            onClick={() => handleDeleteClick(data)}
          />
        </div>
      ),
    },
  ]

  useMemo(() => {
    if (data) {
      const enrichedTableData = data.map((sport: SportListingType) => {
        return {
          Key: sport._id,
          name: sport.name,
          updatedAt: sport.updatedAt,
          icon: sport.icon.thumb,
          actions: sport,
        }
      })
      setTableData(enrichedTableData)
    }
  }, [data])

  useEffect(() => {
    refetch()
  }, [id])

  return (
    <>
      <div>
        {isLoading || isFetching ? (
          <div className="w-full h-70vh flex align-center justify-center">
            <Spinner />
          </div>
        ) : (
          <Table
            className="colored_header_table"
            columns={columns}
            dataSource={tableData}
            pagination={false}
          />
        )}
      </div>

      {alertModal && (
        <AlertModal
          isModalVisible={alertModal}
          setIsModalVisible={setAlertModal}
          data={{
            heading: 'Delete League Sport',
            message1:
              'Are you sure you want to delete this league sport? This action cannot be undone.',
            okText: 'Yes, delete',
          }}
          onYesClick={handleYesClick}
          loading={leagueDeleteLoading}
        />
      )}
    </>
  )
}

export default LeagueSportsTable
